<script lang="ts">
    import { onUnmounted, watchEffect, inject } from 'vue';
    import { JourneyNode } from '@journeys/Journeys.viewmodel';
    import { NodeType } from '@journeys/Enums';

    // components
    import FluidToggle from '@shared/components/FluidToggle.vue';
    import { onMounted } from 'vue';
    import { vue_ko_sync } from '@shared/KoSync';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { SegmentAccess } from '@cwd/Views/Segments/Segments.access';
    import SegmentGroup from '@cwd/Views/Segments/SegmentEditor/SegmentTypes/SegmentGroup.vue';
    import { FeatureService } from '@shared/directives/Feature.service';
    import SegmentsAdd from '@cwd/Views/Segments/SegmentEditor/SegmentsAdd.vue';

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'Tab_NodeSettings_Data',
        disposeWatchers: () => {},
    };

    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    const props = defineProps<{
        node: JourneyNode;
    }>();

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const segmentAccess: SegmentAccess = injectorAccess.injector.get(SegmentAccess);
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);
    if (!segmentAccess.state.segmentTypes) segmentAccess.initWithoutSegmentId();

    props.node.data.segment ||= {
        Type: 'SegmentGroup',
        Bool: 1,
        Segments: [],
    };

    props.node.data.segment.DragAndDropId ||= crypto.randomUUID();

    onMounted(() => {
        let latest_id = props.node.id;
        let localWatcher = watchEffect(() => {
            if (latest_id === props.node.id) return;

            component.disposeWatchers();

            latest_id = props.node.id;
        });

        onUnmounted(() => {
            localWatcher();
            component.disposeWatchers();
        });
    });
</script>

<template>
    <h3 class="h4 m-0-t">{{ $Resources.NodeConditionsHeading }}</h3>

    <template v-if="node.data.type !== NodeType.Task">
        <FluidToggle
            class="slide rounded mb-sm"
            v-model:value="node.data.delay_d"
            :checked="node.data.delay_d || 7"
            :unchecked="0"
            :text="$Resources.GenericDelayLabel"
            multiple="false" />

        <div class="form-group d-flex gap-lg" v-if="node.data.delay_d">
            <div class="d-flex flex-direction-rowrev gap-sm align-items-center">
                <label class="text-nowrap" :for="'delayDays-' + $.uid">{{ $Resources.DelayLabelDays }}</label>
                <input
                    :id="'delayDays-' + $.uid"
                    class="form-control"
                    type="number"
                    @change="(ev: any) => (node.data.delay_d = parseInt(ev.target.value))"
                    :value="node.data.delay_d" />
            </div>

            <div class="d-flex flex-direction-rowrev gap-sm align-items-center">
                <label class="text-nowrap" :for="'delayHours-' + $.uid">{{ $Resources.DelayLabelHours }}</label>
                <input
                    :id="'delayHours-' + $.uid"
                    class="form-control"
                    type="number"
                    @change="(ev: any) => (node.data.delay_h = parseInt(ev.target.value))"
                    :value="node.data.delay_h || 0" />
            </div>

            <template v-if="featureAccess.hasFeature('BackOffice')">
                <div class="d-flex flex-direction-rowrev gap-sm align-items-center">
                    <label class="text-nowrap" :for="'delayMinutes-' + $.uid">{{ $Resources.DelayLabelMinutes }}</label>
                    <input
                        :id="'delayMinutes-' + $.uid"
                        class="form-control"
                        type="number"
                        @change="(ev: any) => (node.data.delay_m = parseInt(ev.target.value))"
                        :value="node.data.delay_m || 0" />
                </div>
            </template>
        </div>
    </template>

    <div class="d-flex gap-sm flex-wrap m-sm-b">
        <SegmentsAdd
            @add="
                (newVal: string) =>
                    node.data.segment.Segments.push({
                        Type: newVal,
                        Bool: 1,
                        Segments: [],
                    })
            " />
    </div>

    <div class="m-lg-b">
        <SegmentGroup v-if="node.data.segment" :segment="node.data.segment" :segment-group="node.data.segment" :root="true"></SegmentGroup>
    </div>
</template>

<style></style>
